import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SubmissionsPanel from './SubmissionsPanel';
import { MediaKitSummary, formatCount } from './MediaKit';
import './AgentConsole.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';
const MIN_PASSWORD = 8;

async function api(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

const EMPTY_FORM = { name: '', email: '', password: '', isSuperAdmin: false };

const PLATFORM_LABELS = {
  instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', facebook: 'Facebook',
  snapchat: 'Snapchat', pinterest: 'Pinterest', x: 'X', twitter: 'X', linkedin: 'LinkedIn',
  twitch: 'Twitch', threads: 'Threads', other: 'Other',
};

const CAMPAIGN_STATUSES = [
  ['open', 'Open'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['closed', 'Closed'],
];
const STATUS_LABEL = Object.fromEntries(CAMPAIGN_STATUSES);

// Edit forms for super-admins. `wide` fields span the whole form row.
const EDIT_FIELDS = {
  campaign: [
    { name: 'title', label: 'Title', required: true },
    { name: 'status', label: 'Status', type: 'select', options: CAMPAIGN_STATUSES },
    { name: 'budget', label: 'Budget (USD)', type: 'number' },
    { name: 'deadline', label: 'Deadline', type: 'date' },
    { name: 'postCount', label: 'Posts', type: 'number' },
    { name: 'storyCount', label: 'Stories', type: 'number' },
    { name: 'creatorCount', label: 'Creators needed', type: 'number' },
    { name: 'minFollowers', label: 'Min. followers', type: 'number' },
    { name: 'description', label: 'Description', type: 'textarea', wide: true },
    { name: 'requirements', label: 'Requirements', type: 'textarea', wide: true },
  ],
  brand: [
    { name: 'email', label: 'Login email', type: 'email', required: true },
    { name: 'companyName', label: 'Company name' },
    { name: 'industry', label: 'Industry' },
    { name: 'location', label: 'Location' },
    { name: 'website', label: 'Website' },
    { name: 'contactEmail', label: 'Contact email', type: 'email' },
    { name: 'description', label: 'About', type: 'textarea', wide: true },
  ],
  creator: [
    { name: 'email', label: 'Login email', type: 'email', required: true },
    { name: 'fullName', label: 'Full name' },
    { name: 'location', label: 'Location' },
    { name: 'instagramHandle', label: 'Instagram handle' },
    { name: 'tiktokHandle', label: 'TikTok handle' },
    { name: 'youtubeHandle', label: 'YouTube' },
    { name: 'julyUrl', label: 'July media kit link', placeholder: 'https://july.bio/username', wide: true },
    { name: 'bio', label: 'Bio', type: 'textarea', wide: true },
  ],
};

const NUMBER_FIELDS = ['budget', 'postCount', 'storyCount', 'creatorCount', 'minFollowers'];

const str = v => (v === null || v === undefined ? '' : String(v));

const matches = (query, ...values) =>
  !query || values.some(v => str(v).toLowerCase().includes(query.trim().toLowerCase()));

// Generic edit form driven by EDIT_FIELDS.
function RecordForm({ fields, record, setRecord, onSubmit, onCancel }) {
  const setValue = (name, value) => setRecord({ ...record, values: { ...record.values, [name]: value } });
  return (
    <form className="ac-form inline" onSubmit={onSubmit}>
      {record.loading ? (
        <p className="ac-muted">Loading…</p>
      ) : (
        <>
          {fields.map(f => (
            <label key={f.name} className={f.wide ? 'wide' : ''}>
              {f.label}
              {f.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={record.values[f.name]}
                  onChange={e => setValue(f.name, e.target.value)}
                />
              ) : f.type === 'select' ? (
                <select value={record.values[f.name]} onChange={e => setValue(f.name, e.target.value)}>
                  {f.options.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type || 'text'}
                  min={f.type === 'number' ? 0 : undefined}
                  value={record.values[f.name]}
                  onChange={e => setValue(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  required={f.required}
                />
              )}
            </label>
          ))}
          {record.error && <div className="error-message wide">{record.error}</div>}
          <div className="ac-form-actions wide">
            <button type="button" className="ac-link" onClick={onCancel} disabled={record.saving}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={record.saving}>
              {record.saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

// Explicit confirmation for permanent deletes: names the item, lists what is
// removed and requires typing DELETE.
function ConfirmDelete({ target, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  const ready = typed.trim().toUpperCase() === 'DELETE';
  return (
    <div className="ac-modal-overlay" onClick={target.busy ? undefined : onCancel}>
      <div className="ac-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <h2>Delete {target.label}?</h2>
        <p>
          This <strong>permanently deletes</strong> {target.label} and can't be undone. It removes:
        </p>
        <ul>
          {target.removes.map(item => <li key={item}>{item}</li>)}
        </ul>
        {target.note && <p className="ac-muted">{target.note}</p>}
        <label className="ac-confirm-input">
          <span>Type <strong>DELETE</strong> to confirm</span>
          <input value={typed} onChange={e => setTyped(e.target.value)} autoFocus />
        </label>
        {target.error && <div className="error-message">{target.error}</div>}
        <div className="ac-modal-actions">
          <button type="button" className="ac-link" onClick={onCancel} disabled={target.busy}>
            Cancel
          </button>
          <button type="button" className="ac-btn danger" onClick={onConfirm} disabled={!ready || target.busy}>
            {target.busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

// July media kit link for a creator under review. Super-admins can set it
// (the API checks the page exists first); the preview lets them confirm it's
// the right person before approving.
function JulyLinkEditor({ accountId, profile, canEdit, onSaved }) {
  const [value, setValue] = useState(profile.julyUrl || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mediaKit = profile.mediaKit;

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = await api(`/api/agent/accounts/${accountId}`, {
        method: 'PUT',
        body: { julyUrl: value.trim() },
      });
      const julyUrl = data.mediaKit?.url || '';
      setValue(julyUrl);
      onSaved({ julyUrl, mediaKit: data.mediaKit || null });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ac-july">
      <div className="ac-july-head">
        <span className="ac-july-title">July media kit</span>
        <span className={`ac-badge ${mediaKit ? 'gold' : 'grey'}`}>{mediaKit ? 'Linked' : 'Not linked'}</span>
      </div>
      {canEdit && (
        <form className="ac-july-form" onSubmit={save}>
          <input
            value={value}
            onChange={e => { setValue(e.target.value); setError(''); }}
            placeholder="https://july.bio/username"
            aria-label="July media kit link"
          />
          <button
            type="submit"
            className="ac-btn"
            disabled={saving || value.trim() === (profile.julyUrl || '')}
          >
            {saving ? 'Checking…' : 'Save link'}
          </button>
        </form>
      )}
      {error && <div className="error-message">{error}</div>}
      {mediaKit ? (
        <MediaKitSummary mediaKit={mediaKit} identity />
      ) : (
        <p className="ac-muted small">
          {canEdit
            ? "Paste the creator's July link (or just their July username) to pull in their reach and recent content."
            : 'No July media kit linked yet.'}
        </p>
      )}
    </div>
  );
}

const timeAgo = iso => {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  if (min < 1440) return `${Math.floor(min / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
};

// Web console for Alooma Agents. Super-admins approve new Creator/Brand
// accounts and manage the agent team here; the full Agent Portal (campaigns,
// creators, message board) lives in the app.
function AgentConsole() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, password }
  const [pending, setPending] = useState([]);
  const [details, setDetails] = useState({}); // accountId -> detail response
  const [openId, setOpenId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creators, setCreators] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState({ campaigns: '', brands: '', creators: '' });
  const [contentOpenId, setContentOpenId] = useState(null); // campaign showing submissions
  // Super-admin edit form: { kind, id, values, original, loading, saving, error }
  const [record, setRecord] = useState(null);
  // Pending delete: { kind, id, label, removes, note, busy, error }
  const [deleting, setDeleting] = useState(null);

  const signOut = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/network');
  }, [navigate]);

  const loadTeam = useCallback(async () => {
    const data = await api('/api/agent/team');
    setAgents(data.agents || []);
  }, []);

  // Every pending account, finished-setup first (web sign-ups can't pick the
  // standardized categories yet, so they may never "complete" setup).
  const loadPending = useCallback(async () => {
    const data = await api('/api/agent/accounts?status=pending&includeIncomplete=true');
    setPending(data.accounts || []);
  }, []);

  const loadCampaigns = useCallback(async () => {
    const data = await api('/api/agent/campaigns');
    setCampaigns(data.campaigns || []);
  }, []);

  const loadCreators = useCallback(async () => {
    const data = await api('/api/agent/creators');
    setCreators(data.creators || []);
  }, []);

  const loadBrands = useCallback(async () => {
    const data = await api('/api/agent/brands');
    setBrands(data.brands || []);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/network');
      return;
    }
    (async () => {
      try {
        const { user } = await api('/api/auth/me');
        if (user.userType !== 'agent') {
          navigate(user.userType === 'brand' ? '/network/brand-dashboard' : '/network/influencer-dashboard');
          return;
        }
        setMe(user);
        if (user.isSuperAdmin) {
          await Promise.all([loadTeam(), loadPending(), loadCampaigns(), loadCreators(), loadBrands()]);
        } else {
          await loadCampaigns();
        }
      } catch (err) {
        if (err.status === 401 || err.status === 403) signOut();
        else setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadTeam, loadPending, loadCampaigns, loadCreators, loadBrands, signOut]);

  const flash = message => {
    setNotice(message);
    setTimeout(() => setNotice(''), 5000);
  };

  const toggleDetails = async account => {
    if (openId === account.id) {
      setOpenId(null);
      return;
    }
    setOpenId(account.id);
    if (!details[account.id]) {
      try {
        const data = await api(`/api/agent/accounts/${account.id}`);
        setDetails(prev => ({ ...prev, [account.id]: data }));
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const review = async (account, decision) => {
    const name = account.name || account.email;
    if (decision === 'deny' && !window.confirm(`Deny ${name}? They will not get access to the platform.`)) return;
    setError('');
    setReviewingId(account.id);
    try {
      await api(`/api/agent/accounts/${account.id}/${decision}`, { method: 'POST' });
      setPending(prev => prev.filter(a => a.id !== account.id));
      flash(`${name} ${decision === 'approve' ? 'approved and notified' : 'denied'}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setReviewingId(null);
    }
  };

  const createAgent = async e => {
    e.preventDefault();
    setError('');
    if (form.password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setSaving(true);
    try {
      const { agent } = await api('/api/agent/team', { method: 'POST', body: form });
      setForm(EMPTY_FORM);
      await loadTeam();
      flash(`${agent.name} can now sign in as an Alooma Agent with ${agent.email}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateAgent = async (agent, changes, message) => {
    setError('');
    try {
      await api(`/api/agent/team/${agent.id}`, { method: 'PATCH', body: changes });
      await loadTeam();
      if (message) flash(message);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const saveEdit = async e => {
    e.preventDefault();
    const agent = agents.find(a => a.id === editing.id);
    if (editing.password && editing.password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    const ok = await updateAgent(
      agent,
      { name: editing.name, password: editing.password || undefined },
      editing.password ? `Password updated for ${editing.name}.` : 'Agent updated.'
    );
    if (ok) setEditing(null);
  };

  const toggleActive = agent => {
    const verb = agent.isActive ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${verb} ${agent.name}?${agent.isActive ? ' They will no longer be able to sign in.' : ''}`)) return;
    updateAgent(agent, { isActive: !agent.isActive }, `${agent.name} ${agent.isActive ? 'deactivated' : 'reactivated'}.`);
  };

  const toggleSuperAdmin = agent => {
    const verb = agent.isSuperAdmin ? 'Remove super-admin access from' : 'Make super-admin:';
    if (!window.confirm(`${verb} ${agent.name}?`)) return;
    updateAgent(agent, { isSuperAdmin: !agent.isSuperAdmin });
  };

  // --- Super-admin edit -----------------------------------------------------

  const startEdit = async (kind, id) => {
    if (record?.kind === kind && record.id === id) {
      setRecord(null);
      return;
    }
    setRecord({ kind, id, values: {}, loading: true });
    try {
      let values;
      if (kind === 'campaign') {
        const { job } = await api(`/api/jobs/${id}`);
        values = {
          title: str(job.title),
          status: job.status || 'open',
          budget: str(job.budget),
          deadline: job.deadline ? String(job.deadline).slice(0, 10) : '',
          postCount: str(job.postCount),
          storyCount: str(job.storyCount),
          creatorCount: str(job.creatorCount),
          minFollowers: str(job.minFollowers),
          description: str(job.description),
          requirements: str(job.requirements),
        };
      } else {
        const { account, profile } = await api(`/api/agent/accounts/${id}`);
        values = { email: str(account.email) };
        EDIT_FIELDS[kind].forEach(f => {
          if (f.name !== 'email') values[f.name] = str(profile?.[f.name]);
        });
      }
      setRecord({ kind, id, values, original: values, loading: false });
    } catch (err) {
      setRecord(null);
      setError(err.message);
    }
  };

  const saveRecord = async e => {
    e.preventDefault();
    const { kind, id, values, original } = record;
    setRecord({ ...record, saving: true, error: '' });
    try {
      if (kind === 'campaign') {
        const body = {};
        Object.entries(values).forEach(([k, v]) => {
          if (k === 'status') return;
          if (NUMBER_FIELDS.includes(k)) body[k] = v === '' ? undefined : Number(v);
          else if (k === 'deadline') body[k] = v || undefined;
          else body[k] = v;
        });
        await api(`/api/jobs/${id}`, { method: 'PUT', body });
        if (values.status !== original.status) {
          await api(`/api/jobs/${id}/status`, { method: 'PATCH', body: { status: values.status } });
        }
        await loadCampaigns();
        flash(`Campaign "${values.title}" updated.`);
      } else {
        await api(`/api/agent/accounts/${id}`, {
          method: 'PUT',
          body: { ...values, email: values.email.trim() },
        });
        setDetails(prev => ({ ...prev, [id]: undefined }));
        await Promise.all([kind === 'brand' ? loadBrands() : loadCreators(), loadPending(), loadCampaigns()]);
        flash(`${kind === 'brand' ? values.companyName || 'Brand' : values.fullName || 'Creator'} updated.`);
      }
      setRecord(null);
    } catch (err) {
      setRecord(prev => prev && { ...prev, saving: false, error: err.message });
    }
  };

  const renderEditForm = (kind, id) =>
    record?.kind === kind && record.id === id && (
      <RecordForm
        fields={EDIT_FIELDS[kind]}
        record={record}
        setRecord={setRecord}
        onSubmit={saveRecord}
        onCancel={() => setRecord(null)}
      />
    );

  // --- Super-admin delete ---------------------------------------------------

  const askDeleteCampaign = c =>
    setDeleting({
      kind: 'campaign',
      id: c.id,
      label: `the campaign "${c.title}" (${c.brandName})`,
      removes: [
        'the campaign and its details',
        `all ${c.bidCount || 0} bid${c.bidCount === 1 ? '' : 's'} on it`,
        'its brand ↔ creator conversations and messages',
        'all content submissions for it',
      ],
    });

  const askDeleteAccount = (kind, a) => {
    const name = kind === 'brand' ? a.companyName : a.fullName;
    setDeleting({
      kind,
      id: a.id,
      label: `the ${kind} "${name || a.email}" (${a.email})`,
      removes: kind === 'brand'
        ? [
            'their login and brand profile',
            `all ${a.campaignCount || 0} campaign${a.campaignCount === 1 ? '' : 's'}, with every bid on them`,
            'their conversations and messages (including with the Alooma team)',
            'content submissions for their campaigns, and their notifications',
          ]
        : [
            'their login and creator profile (rates, connected socials)',
            'all of their bids',
            'their conversations and messages (including with the Alooma team)',
            'their content submissions and notifications',
          ],
    });
  };

  const askDeleteAgent = agent =>
    setDeleting({
      kind: 'agent',
      id: agent.id,
      label: `the agent "${agent.name}" (${agent.email})`,
      removes: ['their Alooma Agent login and team membership'],
      note: 'Messages they sent stay in their threads. Deactivate instead if they may need access again.',
    });

  const confirmDelete = async () => {
    const { kind, id, label } = deleting;
    setDeleting({ ...deleting, busy: true, error: '' });
    try {
      const path = {
        campaign: `/api/jobs/${id}`,
        brand: `/api/agent/accounts/${id}`,
        creator: `/api/agent/accounts/${id}`,
        agent: `/api/agent/team/${id}`,
      }[kind];
      await api(path, { method: 'DELETE' });
      setDeleting(null);
      if (record?.id === id) setRecord(null);
      if (contentOpenId === id) setContentOpenId(null);
      if (openId === id) setOpenId(null);
      // Account deletes cascade to campaigns/bids, so refresh everything affected
      await Promise.all(
        kind === 'agent'
          ? [loadTeam()]
          : kind === 'campaign'
            ? [loadCampaigns(), loadBrands()]
            : [loadCampaigns(), loadBrands(), loadCreators(), loadPending()]
      );
      flash(`Deleted ${label}.`);
    } catch (err) {
      setDeleting(prev => prev && { ...prev, busy: false, error: err.message });
    }
  };

  const filteredCampaigns = campaigns.filter(c => matches(search.campaigns, c.title, c.brandName, c.status));
  const filteredBrands = brands.filter(b => matches(search.brands, b.companyName, b.email, b.industry, b.location));
  const filteredCreators = creators.filter(c =>
    matches(search.creators, c.fullName, c.email, c.instagramHandle, c.location)
  );

  const searchBox = (key, placeholder) => (
    <input
      className="ac-search"
      type="search"
      value={search[key]}
      onChange={e => setSearch({ ...search, [key]: e.target.value })}
      placeholder={placeholder}
    />
  );

  const campaignsSection = (
    <section className="ac-card">
      <div className="ac-card-head">
        <h2>Campaigns ({campaigns.length})</h2>
        {campaigns.length > 5 && searchBox('campaigns', 'Search campaigns or brands')}
      </div>
      {filteredCampaigns.length === 0 ? (
        <p className="ac-muted">{campaigns.length ? 'No campaigns match your search.' : 'No campaigns yet.'}</p>
      ) : (
        <ul className="ac-team">
          {filteredCampaigns.map(c => (
            <li key={c.id}>
              <div className="ac-row">
                <div className="ac-who">
                  <strong>{c.title}</strong>
                  <span className="ac-muted">{c.brandName}</span>
                  <span className="ac-tags">
                    <span className={`ac-badge ${c.status === 'open' ? 'gold' : 'grey'}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    <span className="ac-muted small">
                      {c.bidCount} bid{c.bidCount === 1 ? '' : 's'}
                      {c.budget ? ` · $${Number(c.budget).toLocaleString()}` : ''}
                      {` · created ${timeAgo(c.createdAt)}`}
                    </span>
                  </span>
                </div>
                <div className="ac-actions">
                  <button
                    type="button"
                    className="ac-link"
                    onClick={() => setContentOpenId(contentOpenId === c.id ? null : c.id)}
                  >
                    {contentOpenId === c.id ? 'Hide content' : 'Content'}
                  </button>
                  {me?.isSuperAdmin && (
                    <>
                      <button type="button" className="ac-link" onClick={() => startEdit('campaign', c.id)}>
                        {record?.kind === 'campaign' && record.id === c.id ? 'Cancel' : 'Edit'}
                      </button>
                      <button type="button" className="ac-link danger" onClick={() => askDeleteCampaign(c)}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              {renderEditForm('campaign', c.id)}
              {contentOpenId === c.id && (
                <div className="ac-details">
                  <SubmissionsPanel
                    role="agent"
                    query={{ jobId: c.id }}
                    emptyText="No content submitted for this campaign yet."
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  if (loading) {
    return (
      <div className="agent-console loading-state">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="agent-console">
      <header className="ac-header">
        <div>
          <p className="ac-eyebrow">Alooma Network</p>
          <h1>Agent Console</h1>
        </div>
        <div className="ac-user">
          <span>
            {me?.fullName}
            {me?.isSuperAdmin && <span className="ac-badge gold">Super-admin</span>}
          </span>
          <button type="button" className="ac-link" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <main className="ac-main">
        {error && <div className="error-message">{error}</div>}
        {notice && <div className="ac-notice">{notice}</div>}

        {!me?.isSuperAdmin ? (
          <>
            <section className="ac-card">
              <h2>Agent Portal</h2>
              <p className="ac-muted">
                Approvals, creators, brands and the Message Board are in the Alooma Network mobile
                app. Sign in there with the <strong>Alooma Agent</strong> option. Ask a super-admin
                if you need team access. Campaigns and their submitted content are below.
              </p>
            </section>
            {campaignsSection}
          </>
        ) : (
          <>
            <section className="ac-card">
              <h2>Pending approvals ({pending.length})</h2>
              {pending.length === 0 ? (
                <p className="ac-muted">
                  No accounts waiting. New brands and creators appear here after they sign up.
                </p>
              ) : (
                <ul className="ac-team">
                  {pending.map(account => {
                    const detail = details[account.id];
                    const profile = detail?.profile || {};
                    const missing = detail?.account?.missingProfileFields || [];
                    const isBrand = account.userType === 'brand';
                    return (
                      <li key={account.id}>
                        <div className="ac-row">
                          <div className="ac-who">
                            <strong>{account.name || 'Unnamed'}</strong>
                            <span className="ac-muted">{account.email}</span>
                            <span className="ac-tags">
                              <span className={`ac-badge ${isBrand ? 'blue' : 'gold'}`}>
                                {isBrand ? 'Brand' : 'Creator'}
                              </span>
                              <span className="ac-muted small">
                                {account.profileCompletedAt
                                  ? `Completed setup ${timeAgo(account.profileCompletedAt)}`
                                  : `Signed up ${timeAgo(account.createdAt)} · profile incomplete`}
                              </span>
                            </span>
                          </div>
                          <div className="ac-actions">
                            <button type="button" className="ac-link" onClick={() => toggleDetails(account)}>
                              {openId === account.id ? 'Hide details' : 'Details'}
                            </button>
                            <button
                              type="button"
                              className="ac-btn"
                              disabled={reviewingId === account.id}
                              onClick={() => review(account, 'approve')}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="ac-link danger"
                              disabled={reviewingId === account.id}
                              onClick={() => review(account, 'deny')}
                            >
                              Deny
                            </button>
                          </div>
                        </div>
                        {openId === account.id && (
                          <div className="ac-details">
                            {!detail ? (
                              <p className="ac-muted">Loading…</p>
                            ) : (
                              <>
                                {missing.length > 0 && (
                                  <p className="ac-warn">Profile still missing: {missing.join(', ')}</p>
                                )}
                                <dl>
                                  {(isBrand
                                    ? [
                                        ['Company', profile.companyName],
                                        ['Industry', profile.industry],
                                        ['Location', profile.location],
                                        ['Website', profile.website],
                                        ['Contact', profile.contactEmail],
                                        ['About', profile.description],
                                      ]
                                    : [
                                        ['Location', profile.location],
                                        ['Categories', (profile.primaryCategories || []).join(', ')],
                                        ['Interests', (profile.interests || []).join(', ')],
                                        ['Platforms', (profile.activePlatforms || []).map(p => PLATFORM_LABELS[p] || p).join(', ')],
                                        ['Instagram', profile.instagramHandle && `@${profile.instagramHandle}`],
                                        ['TikTok', profile.tiktokHandle && `@${profile.tiktokHandle}`],
                                        ['YouTube', profile.youtubeHandle],
                                        ['Bio', profile.bio],
                                      ]
                                  )
                                    .filter(([, v]) => v)
                                    .map(([k, v]) => (
                                      <React.Fragment key={k}>
                                        <dt>{k}</dt>
                                        <dd>{v}</dd>
                                      </React.Fragment>
                                    ))}
                                </dl>
                                {!isBrand && (
                                  <JulyLinkEditor
                                    accountId={account.id}
                                    profile={profile}
                                    canEdit={me?.isSuperAdmin}
                                    onSaved={({ julyUrl, mediaKit }) => {
                                      setDetails(prev => ({
                                        ...prev,
                                        [account.id]: {
                                          ...prev[account.id],
                                          profile: { ...prev[account.id].profile, julyUrl, mediaKit },
                                        },
                                      }));
                                      loadCreators().catch(() => {});
                                      flash(julyUrl ? `July media kit linked for ${account.name || account.email}.` : 'July link removed.');
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {campaignsSection}

            <section className="ac-card">
              <div className="ac-card-head">
                <h2>Brands ({brands.length})</h2>
                {brands.length > 5 && searchBox('brands', 'Search brands')}
              </div>
              {filteredBrands.length === 0 ? (
                <p className="ac-muted">{brands.length ? 'No brands match your search.' : 'No brands yet.'}</p>
              ) : (
                <ul className="ac-team">
                  {filteredBrands.map(b => (
                    <li key={b.id}>
                      <div className="ac-row">
                        <div className="ac-who">
                          <strong>{b.companyName || 'Unnamed brand'}</strong>
                          <span className="ac-muted">{b.email}</span>
                          <span className="ac-tags">
                            {b.approvalStatus !== 'approved' && (
                              <span className={`ac-badge ${b.approvalStatus === 'denied' ? 'red' : 'grey'}`}>
                                {b.approvalStatus}
                              </span>
                            )}
                            <span className="ac-muted small">
                              {[b.industry, b.location].filter(Boolean).join(' · ')}
                              {b.industry || b.location ? ' · ' : ''}
                              {b.campaignCount} campaign{b.campaignCount === 1 ? '' : 's'}
                            </span>
                          </span>
                        </div>
                        <div className="ac-actions">
                          <button type="button" className="ac-link" onClick={() => startEdit('brand', b.id)}>
                            {record?.kind === 'brand' && record.id === b.id ? 'Cancel' : 'Edit'}
                          </button>
                          <button type="button" className="ac-link danger" onClick={() => askDeleteAccount('brand', b)}>
                            Delete
                          </button>
                        </div>
                      </div>
                      {renderEditForm('brand', b.id)}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ac-card">
              <div className="ac-card-head">
                <h2>Creators ({creators.length})</h2>
                {creators.length > 5 && searchBox('creators', 'Search creators')}
              </div>
              {filteredCreators.length === 0 ? (
                <p className="ac-muted">{creators.length ? 'No creators match your search.' : 'No creators yet.'}</p>
              ) : (
                <ul className="ac-team">
                  {filteredCreators.map(c => (
                    <li key={c.id}>
                      <div className="ac-row">
                        <div className="ac-who">
                          <strong>{c.fullName || 'Unnamed creator'}</strong>
                          <span className="ac-muted">{c.email}</span>
                          <span className="ac-tags">
                            {c.approvalStatus !== 'approved' && (
                              <span className={`ac-badge ${c.approvalStatus === 'denied' ? 'red' : 'grey'}`}>
                                {c.approvalStatus}
                              </span>
                            )}
                            <span className="ac-muted small">
                              {[c.instagramHandle && `@${c.instagramHandle}`, c.location].filter(Boolean).join(' · ')}
                              {c.instagramHandle || c.location ? ' · ' : ''}
                              {c.mediaKit
                                ? `${formatCount(c.mediaKit.totalFollowers)} reach`
                                : `${(c.followerCount || 0).toLocaleString()} followers`}
                            </span>
                            {c.mediaKit && (
                              <a
                                className="ac-badge gold ac-july-badge"
                                href={c.mediaKit.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={c.mediaKit.url}
                              >
                                July ↗
                              </a>
                            )}
                          </span>
                        </div>
                        <div className="ac-actions">
                          <button type="button" className="ac-link" onClick={() => startEdit('creator', c.id)}>
                            {record?.kind === 'creator' && record.id === c.id ? 'Cancel' : 'Edit'}
                          </button>
                          <button type="button" className="ac-link danger" onClick={() => askDeleteAccount('creator', c)}>
                            Delete
                          </button>
                        </div>
                      </div>
                      {renderEditForm('creator', c.id)}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ac-card">
              <h2>Add an agent</h2>
              <form className="ac-form" onSubmit={createAgent}>
                <label>
                  Name
                  <input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    placeholder="Jordan Rivera"
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    placeholder="jordan@alooma.mg"
                  />
                </label>
                <label>
                  Temporary password
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={MIN_PASSWORD}
                    placeholder={`At least ${MIN_PASSWORD} characters`}
                    autoComplete="new-password"
                  />
                </label>
                <label className="ac-check">
                  <input
                    type="checkbox"
                    checked={form.isSuperAdmin}
                    onChange={e => setForm({ ...form, isSuperAdmin: e.target.checked })}
                  />
                  Super-admin (can manage other agents)
                </label>
                <button type="submit" className="submit-btn" disabled={saving}>
                  {saving ? 'Creating…' : 'Create agent'}
                </button>
              </form>
            </section>

            <section className="ac-card">
              <h2>Alooma team ({agents.length})</h2>
              <ul className="ac-team">
                {agents.map(agent => {
                  const isSelf = agent.id === me.id;
                  const isEditing = editing?.id === agent.id;
                  return (
                    <li key={agent.id} className={agent.isActive ? '' : 'inactive'}>
                      <div className="ac-row">
                        <div className="ac-who">
                          <strong>{agent.name}{isSelf ? ' (you)' : ''}</strong>
                          <span className="ac-muted">{agent.email}</span>
                          <span className="ac-tags">
                            {agent.isSuperAdmin && <span className="ac-badge gold">Super-admin</span>}
                            {!agent.isActive && <span className="ac-badge red">Inactive</span>}
                            {agent.createdByName && (
                              <span className="ac-muted small">Added by {agent.createdByName}</span>
                            )}
                          </span>
                        </div>
                        <div className="ac-actions">
                          <button
                            type="button"
                            className="ac-link"
                            onClick={() => setEditing(isEditing ? null : { id: agent.id, name: agent.name, password: '' })}
                          >
                            {isEditing ? 'Cancel' : 'Edit'}
                          </button>
                          {!isSelf && (
                            <>
                              <button type="button" className="ac-link" onClick={() => toggleSuperAdmin(agent)}>
                                {agent.isSuperAdmin ? 'Remove super-admin' : 'Make super-admin'}
                              </button>
                              <button
                                type="button"
                                className={`ac-link ${agent.isActive ? 'danger' : ''}`}
                                onClick={() => toggleActive(agent)}
                              >
                                {agent.isActive ? 'Deactivate' : 'Reactivate'}
                              </button>
                              <button type="button" className="ac-link danger" onClick={() => askDeleteAgent(agent)}>
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {isEditing && (
                        <form className="ac-form inline" onSubmit={saveEdit}>
                          <label>
                            Name
                            <input
                              value={editing.name}
                              onChange={e => setEditing({ ...editing, name: e.target.value })}
                              required
                            />
                          </label>
                          <label>
                            New password <span className="ac-muted small">(leave blank to keep)</span>
                            <input
                              type="password"
                              value={editing.password}
                              onChange={e => setEditing({ ...editing, password: e.target.value })}
                              placeholder={`At least ${MIN_PASSWORD} characters`}
                              autoComplete="new-password"
                            />
                          </label>
                          <button type="submit" className="submit-btn">Save</button>
                        </form>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          </>
        )}
      </main>

      {deleting && (
        <ConfirmDelete
          key={`${deleting.kind}-${deleting.id}`}
          target={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

export default AgentConsole;
